import React from "react";
import {
    Drawer,
    Box,
    IconButton,
    Typography,
    Card,
    CardContent,
    Avatar,
    CircularProgress,
    Paper,
    Grid,
    Tooltip,
    Modal,
    Button,
} from "@mui/material";
import {
    ArrowBack,
    Download,
    Visibility,
} from "@mui/icons-material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import OuterDiv from "components/Common/OuterDivComponent";
import { AtsCandidate } from "./AtsCandidates";
import { useResumeHandler } from "./useResumeHandler";

interface AtsLeadDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    candidate: AtsCandidate | null;
}

const AtsLeadDetailsDrawer: React.FC<AtsLeadDetailsDrawerProps> = ({
    open,
    onClose,
    candidate,
}) => {
    const {
        showResumeModal,
        signedResumeUrl,
        isDocx,
        loading,
        docxContainerRef,
        handleViewResume,
        handleDownloadResume,
        handleCloseResumeModal,
    } = useResumeHandler();

    const handleClose = () => {
        onClose();
    };

    if (!candidate) return null;

    const evaluationDetails = candidate.evaluation_details;

    const sections = [
        { title: "Strengths", key: "match_strengths" },
        { title: "Match Gaps", key: "match_gaps" },
        { title: "Recommendations", key: "recommendation" },
        { title: "Growth Potential", key: "growth_potential" },
        { title: "Risk Factors", key: "risk_factors" },
        { title: "Cultural Fit", key: "cultural_fit" },
        { title: "Interview / Evaluation focus", key: "interview_focus" },
    ];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            transitionDuration={500}
            PaperProps={{
                sx: {
                    width: "94.5%",
                    background: "#F5F7F9",
                    marginTop: "3%",
                },
            }}
        >
            <OuterDiv style={{ marginLeft: 0, width: "100%", top: 0 }}>
                <Box sx={{ paddingBottom: "10px" }}>
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            background: "#fff",
                        }}
                    >
                        <IconButton size="small" sx={{ mr: 1 }} onClick={handleClose}>
                            <ArrowBack fontSize="small" />
                        </IconButton>

                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                        {candidate.title}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {candidate.email_addresses?.[0]?.value || "N/A"}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ p: 2 }}>
                        {/* Profile Card */}
                        <Card sx={{ mb: 2, border: "1px solid #D9DDE3" }}>
                            <CardContent
                                sx={{ display: "flex", alignItems: "center", gap: 2 }}
                            >
                                <Avatar
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        bgcolor: "#5175e0",
                                        fontSize: "24px",
                                        fontWeight: 600
                                    }}
                                >
                                    {candidate.first_name[0]}{candidate.last_name[0]}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                                        {candidate.first_name} {candidate.last_name}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {candidate.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Tooltip
                                            title={!candidate.resume_url ? "No resume available" : ""}
                                            arrow
                                        >
                                            <span>
                                                <IconButton
                                                    onClick={() => candidate && handleViewResume(candidate)}
                                                    size="small"
                                                    disabled={!candidate.resume_url}
                                                    sx={{
                                                        bgcolor: candidate.resume_url ? "#5175e0" : "#e0e0e0",
                                                        color: candidate.resume_url ? "white" : "#9e9e9e",
                                                        "&:hover": {
                                                            bgcolor: candidate.resume_url ? "#3d5fc4" : "#e0e0e0",
                                                        },
                                                        "&.Mui-disabled": {
                                                            bgcolor: "#e0e0e0",
                                                            color: "#9e9e9e",
                                                        },
                                                        borderRadius: "6px",
                                                        px: 1.5,
                                                        py: 0.5,
                                                    }}
                                                >
                                                    <Visibility sx={{ fontSize: 18, mr: 0.5 }} />
                                                    <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 2 }}>
                                                        Show Resume
                                                    </Typography>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip
                                            title={!candidate.resume_url ? "No resume available" : ""}
                                            arrow
                                        >
                                            <span>
                                                <IconButton
                                                    onClick={() => candidate && handleDownloadResume(candidate)}
                                                    size="small"
                                                    disabled={!candidate.resume_url}
                                                    sx={{
                                                        bgcolor: candidate.resume_url ? "#5175e0" : "#e0e0e0",
                                                        color: candidate.resume_url ? "white" : "#9e9e9e",
                                                        "&:hover": {
                                                            bgcolor: candidate.resume_url ? "#3d5fc4" : "#e0e0e0",
                                                        },
                                                        "&.Mui-disabled": {
                                                            bgcolor: "#e0e0e0",
                                                            color: "#9e9e9e",
                                                        },
                                                        borderRadius: "6px",
                                                        px: 1.5,
                                                        py: 0.5,
                                                    }}
                                                >
                                                    <Download sx={{ fontSize: 18, mr: 0.5 }} />
                                                    <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 2 }}>
                                                        Download
                                                    </Typography>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                    }}
                                >
                                    {evaluationDetails?.candidate_score !== undefined && (
                                        <Box
                                            sx={{ position: "relative", display: "inline-flex" }}
                                        >
                                            <CircularProgress
                                                variant="determinate"
                                                value={evaluationDetails.candidate_score}
                                                size={64}
                                                thickness={4}
                                                sx={{
                                                    color: evaluationDetails.candidate_score > 80
                                                        ? "#4caf50"
                                                        : evaluationDetails.candidate_score >= 50
                                                            ? "#ff9800"
                                                            : "#f44336"
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    position: "absolute",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    component="div"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: "bold", fontSize: "14px" }}
                                                >
                                                    {Math.round(evaluationDetails.candidate_score)}%
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Score
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* AI Summary & Feedback Tab Content */}
                        <Box
                            sx={{
                                background: "#fff",
                                p: 2,
                                borderRadius: "6px",
                                mb: 3,
                                border: "1px solid #D9DDE3",
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                AI Summary & Feedback
                            </Typography>

                            {sections.map((section, index) => {
                                const content = evaluationDetails?.[section.key as keyof typeof evaluationDetails];
                                if (!content) return null;

                                return (
                                    <Paper
                                        key={index}
                                        elevation={0}
                                        sx={{
                                            mb: 1,
                                            p: 2,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <Grid container spacing={2} alignItems="flex-start">
                                            <Grid item>
                                                <Avatar sx={{
                                                    bgcolor: 'transparent',
                                                    width: 24,
                                                    height: 24
                                                }}>
                                                    <AutoAwesomeIcon sx={{ color: '#666', fontSize: 20 }} />
                                                </Avatar>
                                            </Grid>
                                            <Grid item xs>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#333',
                                                        fontSize: '14px',
                                                        mb: 1
                                                    }}
                                                >
                                                    {section.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#666',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    {content as string}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </OuterDiv>

            {/* Resume Modal */}
            <Modal
                open={showResumeModal}
                onClose={handleCloseResumeModal}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: 700,
                        background: '#ffffff',
                        padding: 3,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        overflowY: 'auto',
                        maxHeight: '90vh',
                    }}
                >
                    <IconButton
                        onClick={() => candidate && handleDownloadResume(candidate)}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: '#325cd6',
                            zIndex: 1,
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#1a3f98',
                            },
                        }}
                    >
                        <Download />
                    </IconButton>
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <CircularProgress />
                        </Box>
                    )}
                    {!isDocx ? (
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                            <Viewer fileUrl={signedResumeUrl || ''} />
                        </Worker>
                    ) : (
                        <Box sx={{
                            '& .docx-wrapper': {
                                flexFlow: 'initial !important',
                                alignItems: 'initial !important',
                            }
                        }}>
                            <div ref={docxContainerRef} style={{ padding: '20px' }} />
                        </Box>
                    )}
                </Box>
            </Modal>
        </Drawer>
    );
};

export default AtsLeadDetailsDrawer;